import axios from "axios";
import React, { useEffect, useState } from "react";
import { BiSolidReport } from "react-icons/bi";
import { timeToSeconds } from "../../../../Utilitie";
import { machineEngChartData } from "../ChartDataHelper";
import GroupedBarChart from "../../../../CHARTS/GroupedBarChart";
const options = [
  "Actual_Progress",
  "Hours_of_Block_Demanded",
  "Hours_of_Block_Granted",
  "No_Of_Blocks_Demanded",
  "No_Of_Blocks_Granted",
  "Short_Fall_In_Progress",
  "Stipulated_Progress",
];
const MachineENGChart = ({ data }) => {
  const urlPrefixBlock = process.env.REACT_APP_URL_BLOCK_PREFIX;
  const [selectedOption, setSelectedOption] = useState("Actual_Progress");
  const [tableData, setTableData] = useState(null);
  const [machineData, setMachineData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [chartData, setChartData] = useState(null);

  const fetchMachineData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${urlPrefixBlock}getmachinetype`);
      if (response.data.status === "ok") {
        setMachineData(response.data.machineTypes);
      }
    } catch (err) {
      console.error("Failed to fetch machine types:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMachineData();
  }, []);

  // Helper function to get financial year from a date
  const getFinancialYear = (dateStr) => {
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = date.getMonth(); // 0-based (0 = January, 11 = December)
    // Financial year: April (3) to March (2) next year
    return month >= 3 ? `${year}` : `${year - 1}`;
  };

  const convertToAssetSummaryFromBlockType = (data, machineData) => {
    const blockTypeToAsset = {};
    const typeToAssetFallback = {};

    // Step 1: Build maps
    for (const dept of machineData) {
      const department = dept.DEPARTMENT;
      for (const type of dept.children || []) {
        for (const assetGroup of type.children || []) {
          const asset = assetGroup.ASSET;
          for (const item of assetGroup.data || []) {
            const blockType = item.ASSET_DISCRIPTION;
            const key = `${department}__${blockType}`;
            blockTypeToAsset[key] = {
              ASSET: asset,
              DEPARTMENT: department,
              TYPE: type.TYPE,
              AssetReadyTime: Number(item.AssetReadyTime),
              Work_Done_Per_Hour: Number(item.Work_Done_Per_Hour),
            };
          }

          // Fallback for TYPE
          const typeKey = `${department}__${type.TYPE}`;
          if (!typeToAssetFallback[typeKey]) {
            typeToAssetFallback[typeKey] = {
              ASSET: "-",
              DEPARTMENT: department,
              TYPE: type.TYPE,
              AssetReadyTime: 0,
              Work_Done_Per_Hour: 0,
            };
          }
        }
      }
    }

    const resultMapByYear = {};

    // Step 2: Aggregate by financial year
    for (const row of data) {
      const financialYear = getFinancialYear(row.reportDate);
      if (!resultMapByYear[financialYear]) {
        resultMapByYear[financialYear] = {};
      }
      const resultMap = resultMapByYear[financialYear];

      const department = row.Department || row.DEPARTMENT || "UNKNOWN";
      const blockType = row.Block_Type;
      const type = row.Type || row.TYPE || "UNKNOWN";

      const key = `${department}__${blockType}`;
      let mapEntry = blockTypeToAsset[key];

      // Fallback to TYPE if no blockType match
      if (!mapEntry) {
        const typeKey = `${department}__${type}`;
        mapEntry = typeToAssetFallback[typeKey];
      }

      // Skip if even TYPE doesn't match
      if (!mapEntry) continue;

      const resultKey = `${mapEntry.DEPARTMENT}__${mapEntry.ASSET}`;
      if (!resultMap[resultKey]) {
        resultMap[resultKey] = {
          DEPARTMENT: mapEntry.DEPARTMENT,
          ASSET: mapEntry.ASSET,
          TYPE: mapEntry.TYPE,
          Actual_Progress: 0,
          Hours_of_Block_Demanded: 0,
          Hours_of_Block_Granted: 0,
          No_Of_Blocks_Demanded: 0,
          No_Of_Blocks_Granted: 0,
          Stipulated_Progress: 0,
          Short_Fall_In_Progress: 0,
        };
      }

      const target = resultMap[resultKey];

      const enggDemandSec = timeToSeconds(row.Engg_Demand);
      const grantedSec = timeToSeconds(row.Availed);
      const noOfGranted = Number(row.No_Of_Demand_Agreed_GIVEN) || 0;
      const assetReadySec = mapEntry.AssetReadyTime || 0;
      const workDonePerHour = mapEntry.Work_Done_Per_Hour || 0;

      target.Actual_Progress += Number(row.Actual_Output_in_Meter) || 0;
      target.Hours_of_Block_Demanded += enggDemandSec;
      target.Hours_of_Block_Granted += grantedSec;
      target.No_Of_Blocks_Demanded += Number(row.No_Of_Demand_By_Engg) || 0;
      target.No_Of_Blocks_Granted += noOfGranted;

      target.Stipulated_Progress +=
        ((grantedSec - noOfGranted * assetReadySec) / 3600) * workDonePerHour;
    }

    // Step 3: Format results for each financial year
    const finalResult = {};
    for (const financialYear in resultMapByYear) {
      finalResult[financialYear] = Object.values(resultMapByYear[financialYear])
        .map((entry) => ({
          ...entry,
          Hours_of_Block_Demanded: entry.Hours_of_Block_Demanded.toString(),
          Hours_of_Block_Granted: entry.Hours_of_Block_Granted.toString(),
          No_Of_Blocks_Demanded: entry.No_Of_Blocks_Demanded.toString(),
          No_Of_Blocks_Granted: entry.No_Of_Blocks_Granted.toString(),
          Stipulated_Progress: entry.Stipulated_Progress.toFixed(2),
          Short_Fall_In_Progress: (
            entry.Actual_Progress - entry.Stipulated_Progress
          ).toFixed(2),
        }))
        .sort((a, b) => {
          if (a.DEPARTMENT < b.DEPARTMENT) return -1;
          if (a.DEPARTMENT > b.DEPARTMENT) return 1;
          if (a.TYPE < b.TYPE) return -1;
          if (a.TYPE > b.TYPE) return 1;
          if (a.ASSET < b.ASSET) return -1;
          if (a.ASSET > b.ASSET) return 1;
          return 0;
        });
    }

    return finalResult;
  };

  useEffect(() => {
    if (machineData && data) {
      const convertedData = convertToAssetSummaryFromBlockType(
        data,
        machineData,
      );
      setTableData(convertedData);
    }
  }, [data, machineData]);
  useEffect(() => {
    if (data && tableData) {
      const arrangedData = machineEngChartData(tableData, selectedOption);
      setChartData(arrangedData);
    }
  }, [tableData, selectedOption]);
  return (
    <div className="papaDiv">
      <div className=" p-1 bg-dark text-white papaDiv d-flex justify-content-between">
        {" "}
        <h5 className="">
          <BiSolidReport />
          Machine (Emg)
          <b style={{ fontSize: "16px", color: "rgba(188, 186, 186, 1)" }}></b>
        </h5>
        <select
          //   value={selectedOption.replace(/_/g, " ")}
          onChange={(e) => {
            setSelectedOption(e.target.value);
          }}
        >
          {/* <option> select any</option> */}
          {options.map((option, index) => {
            return (
              <option key={index} value={option}>
                {option.replace(/_/g, " ")}
              </option>
            );
          })}
        </select>
      </div>
      <h4>{selectedOption.replace(/_/g, " ")}</h4>
      <hr className="m-0 mb-1 p-0" />
      {chartData && chartData.series.length > 0 ? (
        <GroupedBarChart
          data={chartData}
          from={`Block_${
            selectedOption === "Hours_of_Block_Granted" ||
            selectedOption === "Hours_of_Block_Demanded"
              ? "T"
              : "C"
          }`}
          chartType="bar"
        />
      ) : (
        "No Data Found"
      )}
    </div>
  );
};
export default MachineENGChart;
